import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { usePostHog } from 'posthog-js/react'
import { artworkApi, CreateArtworkData, UpdateArtworkData } from '../api/artworkApi'
import { ArtworkStatus } from '@/types/entities'
import type { Artwork } from '@/types/entities'
import type { PaginatedResponse, PaginationParams } from '@/types/common'
import { useToast } from '@/hooks/use-toast'
import { trackArtworkPublished } from '@/lib/analytics'
import { getErrorMessage } from '@/lib/utils'

function updateArtworkCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  artworkId: number,
  patch: Partial<Artwork>
) {
  queryClient.setQueryData<Artwork>(['artwork', artworkId], (current) =>
    current ? { ...current, ...patch } : current
  )

  queryClient.setQueriesData<PaginatedResponse<Artwork>>(
    { queryKey: ['artworks'] },
    (current) => {
      if (!current) return current
      return {
        ...current,
        data: current.data.map((artwork) =>
          artwork.id === artworkId ? { ...artwork, ...patch } : artwork
        ),
      }
    }
  )
}

export function useArtworks(status: ArtworkStatus, pagination: PaginationParams) {
  return useQuery({
    queryKey: ['artworks', status, pagination],
    queryFn: () => artworkApi.getArtworks(status, pagination),
  })
}

export function useArtwork(artworkId: number) {
  return useQuery({
    queryKey: ['artwork', artworkId],
    queryFn: () => artworkApi.getArtwork(artworkId),
    enabled: !!artworkId,
  })
}

export function useArtworkCount(status?: ArtworkStatus) {
  return useQuery({
    queryKey: ['artworks', 'count', status],
    queryFn: () => artworkApi.getArtworkCount(status),
  })
}

export function useCreateArtwork() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: CreateArtworkData) => artworkApi.createArtwork(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['artworks'] })
      toast({
        title: 'Success',
        description: 'Artwork created successfully',
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to create artwork',
        variant: 'destructive',
      })
    },
  })
}

export function useUpdateArtwork() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateArtworkData }) =>
      artworkApi.updateArtwork(id, data),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ['artwork', variables.id] })
      const previousArtwork = queryClient.getQueryData<Artwork>(['artwork', variables.id])
      const previousArtworks = queryClient.getQueriesData<PaginatedResponse<Artwork>>({
        queryKey: ['artworks'],
      })

      // Tags go up as ids and come back as objects, so they are left to the server's answer.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { tags: _tagIds, ...patch } = variables.data
      updateArtworkCaches(queryClient, variables.id, patch)

      return { previousArtwork, previousArtworks }
    },
    onError: (_error, variables, context) => {
      // Rollback optimistic updates
      if (context?.previousArtwork) {
        queryClient.setQueryData(['artwork', variables.id], context.previousArtwork)
      }
      if (context?.previousArtworks) {
        context.previousArtworks.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      // Show error toast
      toast({
        title: 'Error',
        description: 'Failed to update artwork',
        variant: 'destructive',
      })
    },
    onSuccess: (updatedArtwork, variables) => {
      if (updatedArtwork) {
        updateArtworkCaches(queryClient, variables.id, updatedArtwork)
      }
      queryClient.invalidateQueries({ queryKey: ['artworks'] })
      queryClient.invalidateQueries({ queryKey: ['artworks', 'count'] })
      queryClient.invalidateQueries({ queryKey: ['artwork', variables.id] })
      toast({
        title: 'Success',
        description: 'Artwork updated successfully',
      })
    },
  })
}

export function useDeleteArtwork() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (artworkId: number) => artworkApi.deleteArtwork(artworkId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['artworks'] })
      toast({
        title: 'Success',
        description: 'Artwork deleted successfully',
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to delete artwork',
        variant: 'destructive',
      })
    },
  })
}

export function useUploadArtworkImage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  // Completed uploads are recorded once by the API and mirrored to PostHog.

  return useMutation({
    mutationFn: ({
      artworkId,
      file,
      onProgress,
    }: {
      artworkId: number
      file: File
      onProgress?: (progress: number) => void
    }) => artworkApi.uploadArtworkImage(artworkId, file, onProgress),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['artwork', variables.artworkId] })
      toast({
        title: 'Success',
        description: 'Image uploaded successfully',
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to upload image',
        variant: 'destructive',
      })
    },
  })
}

export function usePublishArtwork() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const posthog = usePostHog()

  return useMutation({
    mutationFn: (artworkId: number) => artworkApi.publishArtwork(artworkId),
    onMutate: async (artworkId) => {
      await queryClient.cancelQueries({ queryKey: ['artwork', artworkId] })
      const previousArtwork = queryClient.getQueryData<Artwork>(['artwork', artworkId])
      const previousArtworks = queryClient.getQueriesData<PaginatedResponse<Artwork>>({
        queryKey: ['artworks'],
      })

      updateArtworkCaches(queryClient, artworkId, { status: ArtworkStatus.PUBLISHED })

      return { previousArtwork, previousArtworks }
    },
    onError: (error, artworkId, context) => {
      console.error('Failed to publish artwork:', error)
      if (context?.previousArtwork) {
        queryClient.setQueryData(['artwork', artworkId], context.previousArtwork)
      }
      if (context?.previousArtworks) {
        context.previousArtworks.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      toast({
        title: 'Error',
        description: `Failed to publish artwork: ${getErrorMessage(error)}`,
        variant: 'destructive',
      })
    },
    onSuccess: (_, artworkId) => {
      updateArtworkCaches(queryClient, artworkId, { status: ArtworkStatus.PUBLISHED })
      queryClient.invalidateQueries({ queryKey: ['artworks'] })
      queryClient.invalidateQueries({ queryKey: ['artworks', 'count'] })
      queryClient.invalidateQueries({ queryKey: ['artwork', artworkId] })
      trackArtworkPublished(posthog, { artwork_id: artworkId })
      toast({
        title: 'Success',
        description: 'Artwork published successfully',
      })
    },
  })
}

export function useUnpublishArtwork() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (artworkId: number) => artworkApi.unpublishArtwork(artworkId),
    onMutate: async (artworkId) => {
      await queryClient.cancelQueries({ queryKey: ['artwork', artworkId] })
      const previousArtwork = queryClient.getQueryData<Artwork>(['artwork', artworkId])
      const previousArtworks = queryClient.getQueriesData<PaginatedResponse<Artwork>>({
        queryKey: ['artworks'],
      })

      updateArtworkCaches(queryClient, artworkId, { status: ArtworkStatus.DRAFT })

      return { previousArtwork, previousArtworks }
    },
    onError: (error, artworkId, context) => {
      console.error('Failed to unpublish artwork:', error)
      if (context?.previousArtwork) {
        queryClient.setQueryData(['artwork', artworkId], context.previousArtwork)
      }
      if (context?.previousArtworks) {
        context.previousArtworks.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      toast({
        title: 'Error',
        description: 'Failed to unpublish artwork',
        variant: 'destructive',
      })
    },
    onSuccess: (_, artworkId) => {
      updateArtworkCaches(queryClient, artworkId, { status: ArtworkStatus.DRAFT })
      queryClient.invalidateQueries({ queryKey: ['artworks'] })
      queryClient.invalidateQueries({ queryKey: ['artworks', 'count'] })
      queryClient.invalidateQueries({ queryKey: ['artwork', artworkId] })
      toast({
        title: 'Success',
        description: 'Artwork unpublished successfully',
      })
    },
  })
}

